<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { ARTICLE_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import FullEditor from 'dashboard/components/widgets/WootWriter/FullEditor.vue';
import { useTicketsStore } from 'dashboard/stores/tickets';
import { useTicketColumnsStore } from 'dashboard/stores/ticketColumns';
import { useTicketCommentsStore } from 'dashboard/stores/ticketComments';
import TicketLabelBox from './TicketLabelBox.vue';

const { t } = useI18n();
const store = useStore();
const ticketsStore = useTicketsStore();
const columnsStore = useTicketColumnsStore();
const commentsStore = useTicketCommentsStore();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const accountId = useMapGetter('getCurrentAccountId');

const PRIORITY_ICON = {
  urgent: 'i-woot-priority-urgent',
  high: 'i-woot-priority-high',
  medium: 'i-woot-priority-medium',
  low: 'i-woot-priority-low',
};

const isOpen = ref(false);
const openedId = ref(null);

const editingTitle = ref(false);
const titleDraft = ref('');
const editingDescription = ref(false);
const descriptionDraft = ref('');
const commentDraft = ref('');

const ticket = computed(() => ticketsStore.getRecord(openedId.value) || {});
const isDeleted = computed(() => Boolean(ticket.value.deleted_at));
const columns = computed(() => columnsStore.getColumns);
const comments = computed(() => commentsStore.forTicket(openedId.value));

const columnOptions = computed(() =>
  columns.value.map(c => ({ id: c.id, name: c.name, icon: 'i-lucide-columns-2' }))
);
const agentOptions = computed(() =>
  agents.value.map(a => ({
    id: a.id,
    name: a.name,
    thumbnail: a.thumbnail || a.avatar_url,
    availability_status: a.availability_status,
  }))
);
const teamOptions = computed(() =>
  teams.value.map(tm => ({ id: tm.id, name: tm.name, icon: 'i-lucide-users' }))
);
const priorityOptions = computed(() => [
  { id: '', name: t('TICKETS.PRIORITY.NONE') },
  ...['urgent', 'high', 'medium', 'low'].map(p => ({
    id: p,
    name: t(`TICKETS.PRIORITY.${p.toUpperCase()}`),
    icon: PRIORITY_ICON[p],
  })),
]);

const selectedColumn = computed(
  () => columnOptions.value.find(o => o.id === ticket.value.ticket_column_id) || {}
);
const selectedAgent = computed(
  () => agentOptions.value.find(o => o.id === ticket.value.assignee_id) || {}
);
const selectedTeam = computed(
  () => teamOptions.value.find(o => o.id === ticket.value.team_id) || {}
);
const selectedPriority = computed(
  () => priorityOptions.value.find(o => o.id === (ticket.value.priority || '')) || {}
);

const renderedDescription = computed(() =>
  ticket.value.description
    ? new MessageFormatter(ticket.value.description).formattedMessage
    : ''
);

const customAttributes = computed(() => {
  const conversation = ticket.value.conversation || {};
  const merged = {
    ...(conversation.custom_attributes || {}),
    ...(conversation.contact_attributes || {}),
  };
  return Object.entries(merged)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({ key, label: key.replace(/_/g, ' '), value: String(value) }));
});

const patch = async partial => {
  try {
    await ticketsStore.update({ id: openedId.value, ticket: partial });
  } catch {
    useAlert(t('TICKETS.UPDATE.ERROR'));
  }
};

const labelsModel = computed({
  get: () => ticket.value.labels || [],
  set: labels => patch({ labels }),
});

const startTitle = () => {
  titleDraft.value = ticket.value.title;
  editingTitle.value = true;
};
const saveTitle = () => {
  editingTitle.value = false;
  const value = titleDraft.value.trim();
  if (value && value !== ticket.value.title) patch({ title: value });
};

const startDescription = () => {
  descriptionDraft.value = ticket.value.description || '';
  editingDescription.value = true;
};
const saveDescription = () => {
  editingDescription.value = false;
  if (descriptionDraft.value !== (ticket.value.description || '')) {
    patch({ description: descriptionDraft.value });
  }
};

const addComment = async () => {
  const content = commentDraft.value.trim();
  if (!content) return;
  try {
    await commentsStore.create(openedId.value, content);
    commentDraft.value = '';
  } catch {
    useAlert(t('TICKETS.DETAIL.COMMENT_ERROR'));
  }
};

const formatComment = content => new MessageFormatter(content || '').formattedMessage;

const deleteTicket = async () => {
  try {
    await ticketsStore.softDelete(openedId.value);
    useAlert(t('TICKETS.DETAIL.DELETE_SUCCESS'));
    close();
  } catch {
    useAlert(t('TICKETS.DETAIL.DELETE_ERROR'));
  }
};

const restoreTicket = async () => {
  try {
    await ticketsStore.restore(openedId.value);
    useAlert(t('TICKETS.DETAIL.RESTORE_SUCCESS'));
  } catch {
    useAlert(t('TICKETS.DETAIL.RESTORE_ERROR'));
  }
};

const onKeydown = e => {
  if (e.key === 'Escape') close();
};

const open = ticketRecord => {
  openedId.value = ticketRecord.id;
  editingTitle.value = false;
  editingDescription.value = false;
  commentDraft.value = '';
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  columnsStore.get();
  commentsStore.fetch(ticketRecord.id);
  isOpen.value = true;
  nextTick(() => document.addEventListener('keydown', onKeydown));
};
const close = () => {
  isOpen.value = false;
  document.removeEventListener('keydown', onKeydown);
};

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9990] flex items-start justify-center p-4 overflow-y-auto bg-n-alpha-black1 backdrop-blur-[4px]"
      @click.self="close"
    >
      <div
        class="w-full max-w-5xl my-auto rounded-xl bg-n-solid-1 shadow-xl outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-between px-4 py-2.5 border-b border-n-weak">
          <div class="flex items-center gap-1.5 text-n-slate-10">
            <Icon icon="i-lucide-ticket" class="size-3.5" />
            <span class="text-xs tabular-nums">#{{ ticket.display_id }}</span>
            <span
              v-if="isDeleted"
              class="px-1.5 py-0.5 text-xs font-medium rounded-full bg-n-ruby-3 text-n-ruby-11"
            >
              {{ t('TICKETS.DETAIL.DELETED_BADGE') }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <Button
              v-if="isDeleted"
              :label="t('TICKETS.DETAIL.RESTORE')"
              icon="i-lucide-rotate-ccw"
              variant="ghost"
              color="slate"
              size="xs"
              @click="restoreTicket"
            />
            <Button
              v-else
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="xs"
              :title="t('TICKETS.DETAIL.DELETE')"
              @click="deleteTicket"
            />
            <Button icon="i-lucide-x" variant="ghost" color="slate" size="xs" @click="close" />
          </div>
        </div>

        <div class="flex flex-col md:flex-row max-h-[82vh]">
          <!-- Artículo -->
          <div class="flex flex-col flex-1 min-w-0 gap-3 p-4 overflow-y-auto overflow-x-hidden">
            <input
              v-if="editingTitle"
              v-model="titleDraft"
              class="w-full text-lg font-semibold bg-transparent border-0 outline-none text-n-slate-12"
              @keyup.enter="saveTitle"
              @keyup.esc="editingTitle = false"
              @blur="saveTitle"
            />
            <h2
              v-else
              class="text-lg font-semibold leading-snug cursor-text text-n-slate-12"
              @click="startTitle"
            >
              {{ ticket.title }}
            </h2>

            <FullEditor
              v-if="editingDescription"
              v-model="descriptionDraft"
              class="text-sm min-w-0 [&_.ProseMirror]:break-words [&_pre]:overflow-x-auto [&_pre]:max-w-full"
              :placeholder="t('TICKETS.DETAIL.DESCRIPTION_PLACEHOLDER')"
              :enabled-menu-options="ARTICLE_EDITOR_MENU_OPTIONS"
              @blur="saveDescription"
            />
            <div
              v-else
              class="min-h-[2.5rem] cursor-text rounded-md p-1.5 -m-1.5 hover:bg-n-alpha-1"
              @click="startDescription"
            >
              <div
                v-if="renderedDescription"
                v-dompurify-html="renderedDescription"
                class="text-sm prose prose-bubble max-w-none break-words [&_pre]:overflow-x-auto [&_pre]:max-w-full"
              />
              <p v-else class="text-sm text-n-slate-10">
                {{ t('TICKETS.DETAIL.DESCRIPTION_EMPTY') }}
              </p>
            </div>

            <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
              <h3 class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                {{ t('TICKETS.DETAIL.COMMENTS') }}
              </h3>
              <ul v-if="comments.length" class="flex flex-col gap-2">
                <li v-for="comment in comments" :key="comment.id" class="flex gap-1.5">
                  <Avatar
                    :name="comment.user?.name || 'Bot'"
                    :src="comment.user?.avatar_url"
                    :size="18"
                    rounded-full
                  />
                  <div class="flex flex-col min-w-0">
                    <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
                      <span class="font-medium text-n-slate-12">
                        {{ comment.user?.name || 'Bot' }}
                      </span>
                      <span>{{ dynamicTime(comment.created_at) }}</span>
                    </div>
                    <div
                      v-dompurify-html="formatComment(comment.content)"
                      class="text-sm prose prose-bubble max-w-none break-words [&_pre]:overflow-x-auto [&_pre]:max-w-full"
                    />
                  </div>
                </li>
              </ul>

              <div class="flex flex-col gap-1.5 p-1.5 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-weak min-w-0">
                <FullEditor
                  v-model="commentDraft"
                  class="text-sm min-w-0 [&_.ProseMirror]:break-words [&_pre]:overflow-x-auto [&_pre]:max-w-full"
                  :placeholder="t('TICKETS.DETAIL.COMMENT_PLACEHOLDER')"
                  :enabled-menu-options="ARTICLE_EDITOR_MENU_OPTIONS"
                />
                <Button
                  :label="t('TICKETS.DETAIL.COMMENT_SEND')"
                  color="blue"
                  size="xs"
                  class="self-end"
                  :disabled="!commentDraft.trim()"
                  @click="addComment"
                />
              </div>
            </div>
          </div>

          <!-- Panel de detalles -->
          <div
            class="flex flex-col gap-2.5 p-4 md:w-60 md:shrink-0 border-t md:border-t-0 md:border-l border-n-weak"
          >
            <div class="flex flex-col gap-0.5">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.COLUMN_LABEL') }}</label>
              <MultiselectDropdown
                :options="columnOptions"
                :selected-item="selectedColumn"
                :has-thumbnail="false"
                :multiselector-title="t('TICKETS.DIALOG.COLUMN_LABEL')"
                :multiselector-placeholder="t('TICKETS.DIALOG.COLUMN_LABEL')"
                @select="item => patch({ ticket_column_id: item.id })"
              />
            </div>
            <div class="flex flex-col gap-0.5">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.ASSIGNEE_LABEL') }}</label>
              <MultiselectDropdown
                :options="agentOptions"
                :selected-item="selectedAgent"
                :multiselector-title="t('TICKETS.DIALOG.ASSIGNEE_LABEL')"
                :multiselector-placeholder="t('TICKETS.LIST.UNASSIGNED')"
                @select="item => patch({ assignee_id: item.id })"
              />
            </div>
            <div class="flex flex-col gap-0.5">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.TEAM_LABEL') }}</label>
              <MultiselectDropdown
                :options="teamOptions"
                :selected-item="selectedTeam"
                :has-thumbnail="false"
                :multiselector-title="t('TICKETS.DIALOG.TEAM_LABEL')"
                :multiselector-placeholder="t('TICKETS.DIALOG.TEAM_NONE')"
                @select="item => patch({ team_id: item.id })"
              />
            </div>
            <div class="flex flex-col gap-0.5">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.PRIORITY_LABEL') }}</label>
              <MultiselectDropdown
                :options="priorityOptions"
                :selected-item="selectedPriority"
                :has-thumbnail="false"
                :multiselector-title="t('TICKETS.DIALOG.PRIORITY_LABEL')"
                :multiselector-placeholder="t('TICKETS.PRIORITY.NONE')"
                @select="item => patch({ priority: item.id || null })"
              />
            </div>
            <div class="flex flex-col gap-0.5">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.LABELS_LABEL') }}</label>
              <TicketLabelBox v-model="labelsModel" />
            </div>

            <div v-if="ticket.conversation_id" class="flex flex-col gap-0.5 pt-2 border-t border-n-weak">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DETAIL.CONVERSATION') }}</label>
              <a
                :href="`/app/accounts/${accountId}/conversations/${ticket.conversation_id}`"
                target="_blank"
                class="flex items-center gap-1.5 text-sm text-n-blue-text hover:underline"
              >
                <Icon icon="i-lucide-message-circle" class="size-4" />
                #{{ ticket.conversation_id }}
                <span v-if="ticket.conversation?.contact_name" class="truncate text-n-slate-11">
                  · {{ ticket.conversation.contact_name }}
                </span>
              </a>
            </div>

            <div v-if="customAttributes.length" class="flex flex-col gap-1 pt-2 border-t border-n-weak">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DETAIL.ATTRIBUTES') }}</label>
              <div
                v-for="attr in customAttributes"
                :key="attr.key"
                class="flex items-center justify-between gap-2 text-xs"
              >
                <span class="capitalize truncate text-n-slate-11">{{ attr.label }}</span>
                <span class="truncate text-right text-n-slate-12">{{ attr.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </TeleportWithDirection>
</template>
